// 决胜一刻 (peak-moment share) — the rules, with no DOM and no network attached
// (same logic/DOM split as ladder.ts vs ui/, TECH_SPEC §4bis.22). Everything
// that decides lives here so the client test suite can pin it without a
// browser; ui/peak.ts and session.ts only ever *apply* these answers.
//
// Three independent things:
//
//   1. `peakEnabled` — the dark-rollout gate, a clone of §4bis.21's
//      `ladderEnabled` under its own salt. At `PEAK_PCT=0` nobody is in and
//      rendering is pixel-identical to today — a tested property.
//   2. `peakFor` / `peakCoverageFor` — run classification. `peakFor` decides
//      whether the seal may show; `peakCoverageFor` is the `result_view`
//      coverage prop, computed on EVERY run (both arms) so coverage data
//      exists before any ramp decision.
//   3. The pinned presentation tables: CTA pool per (kind, entry), stamp
//      clamps, the submit-wait ceiling and the fail/dismiss decisions.

import { hashString, mix32 } from './rng';
import type { EntryType } from './types';

/**
 * Rollout salt. Deliberately NOT `card-ab-v1` (experiment.ts) and NOT
 * `rival-ladder-v1` (ladder.ts): the same device id under a different salt is
 * an *independent* assignment, so being in the peak arm tells you nothing
 * about the other two experiments. Pinned by a golden vector in the tests.
 */
export const PEAK_ID = 'peak-v1';

/** Stable bucket 0–99 for a device: `mix32(fnv1a("peak-v1:" + id)) % 100`. */
export function peakBucket(deviceId: string): number {
  return mix32(hashString(`${PEAK_ID}:${deviceId}`)) % 100;
}

/**
 * Is this device in the peak arm? `pct` is the server's `peakPct` (0 = dark,
 * 100 = everyone). Anything unparseable is 0: fail closed on both wire ends.
 */
export function peakEnabled(deviceId: string, pct: number | undefined | null): boolean {
  if (typeof pct !== 'number' || !Number.isFinite(pct) || pct <= 0) return false;
  if (pct >= 100) return true;
  return peakBucket(deviceId) < pct;
}

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------

/** A loss within this many frames of the opponent's end is a 差一口气 near
 *  loss — 2.0 s at 60 Hz, the same delta `result.deltaSeconds` shows.
 *  Pre-registered constant (design spec DR-R2); changing it changes coverage. */
export const PEAK_NEARLOSS_FRAMES = 120;

/** The two run endings that earn the seal. Everything else: zero change. */
export type PeakKind = 'win' | 'nearloss';

/**
 * Seal classification (pure). `oppEndFrame` is the opponent's death frame, or
 * MAX_FRAMES when they finished — exactly what `result.deltaSeconds` uses.
 * `playerDeathFrame` follows the same convention for a finished player.
 *
 *   'win'      outcome is 'win' (by meters, so finished runs count)
 *   'nearloss' outcome is 'lose', the player died, and they were
 *              1..PEAK_NEARLOSS_FRAMES frames short of the opponent's end
 *   null       tie, clear loss, survived-but-behind
 */
export function peakFor(
  outcome: 'win' | 'lose' | 'tie',
  playerDead: boolean,
  playerDeathFrame: number,
  oppEndFrame: number,
): PeakKind | null {
  if (outcome === 'win') return 'win';
  if (outcome !== 'lose' || !playerDead) return null;
  const delta = oppEndFrame - playerDeathFrame;
  return delta >= 1 && delta <= PEAK_NEARLOSS_FRAMES ? 'nearloss' : null;
}

/** `result_view` coverage prop. Ties and (future) finished-but-lost runs get
 *  their OWN buckets, never folded into `none` — they pre-load the read for a
 *  trigger-widening follow-up (DR-R2). Sent on every run, both arms. */
export type PeakCoverage = PeakKind | 'tie' | 'finish' | 'none';

export function peakCoverageFor(
  outcome: 'win' | 'lose' | 'tie',
  playerDead: boolean,
  playerDeathFrame: number,
  oppEndFrame: number,
  finished: boolean,
): PeakCoverage {
  const kind = peakFor(outcome, playerDead, playerDeathFrame, oppEndFrame);
  if (kind !== null) return kind;
  if (outcome === 'tie') return 'tie';
  if (finished) return 'finish';
  return 'none';
}

// ---------------------------------------------------------------------------
// The seal's decision table (pure halves of ui/peak.ts + session.ts)
// ---------------------------------------------------------------------------

/** The overlay may hold the death frame for at most this long waiting on the
 *  submit; past it, auto-dismiss to the result screen (DR-R2). */
export const PEAK_SUBMIT_CEILING_MS = 4500;

/** Overlay timeline (wall-clock ms from death). */
export const PEAK_STAMP_MS = 250;
export const PEAK_TAP_ARM_MS = 400;
export const PEAK_CTA_MS = 1000;
/** CTA input-arms only once its 200 ms slide-up has finished (~t=1200 ms). */
export const PEAK_CTA_SLIDE_MS = 200;

export interface SealGate {
  /** The arm, as computed once at session start (debug forcing included). */
  peakActive: boolean;
  kind: PeakKind | null;
  /** A bot, not a thumb, drove this run. */
  autoplay: boolean;
  /** Debug `&peakui=1`: the harness explicitly asked for the overlay. */
  showPeak: boolean;
  /** `navigator.onLine !== false` at death time — a submit that cannot even
   *  start must not mount an overlay that would only vanish again. */
  online: boolean;
}

/** Should `endRun` mount the seal instead of the result screen? */
export function shouldShowSeal(g: SealGate): boolean {
  if (!g.peakActive || g.kind === null) return false;
  if (g.autoplay && !g.showPeak) return false;
  return g.online;
}

/** What a failed submit does to a mounted overlay: before the CTA is revealed
 *  the overlay auto-dismisses (the result screen owns the offline note); after,
 *  the CTA row downgrades to the dismiss hint. No dead-end state exists. */
export function onSubmitFail(ctaRevealed: boolean): 'dismiss' | 'downgrade' {
  return ctaRevealed ? 'downgrade' : 'dismiss';
}

/**
 * The pinned entryType → CTA-pool table (zero implementer latitude, DR-R2):
 *
 *   win      + link entry                      → pool.peakCtaBack   (send it back)
 *   win      + walk-in/first/rerun             → pool.peakCtaFresh  (drag someone in)
 *   nearloss + link/rerun vs a real sender     → pool.peakCtaNear
 *   nearloss + walk-in/first or official ghost → pool.peakCtaNearWalkin
 *                                                (no sender to reply to, DR-R1)
 */
export type PeakCtaPool =
  | 'pool.peakCtaBack'
  | 'pool.peakCtaFresh'
  | 'pool.peakCtaNear'
  | 'pool.peakCtaNearWalkin';

export function peakCtaPool(
  kind: PeakKind,
  entryType: EntryType,
  officialOpponent: boolean,
): PeakCtaPool {
  if (kind === 'win') return entryType === 'link' ? 'pool.peakCtaBack' : 'pool.peakCtaFresh';
  const realSender = (entryType === 'link' || entryType === 'rerun') && !officialOpponent;
  return realSender ? 'pool.peakCtaNear' : 'pool.peakCtaNearWalkin';
}

// ---------------------------------------------------------------------------
// Stamp clamps (giant type must never wrap)
// ---------------------------------------------------------------------------

/** Nicknames longer than this many code points clamp to 8 + 「…」 inside the
 *  stamp — a 4-digit {d} plus a long name must never wrap the giant type. */
export const PEAK_NAME_MAX = 8;

export function clampStampName(name: string): string {
  const points = [...name];
  return points.length > PEAK_NAME_MAX ? `${points.slice(0, PEAK_NAME_MAX).join('')}…` : name;
}

/** The stamp drops one type step when the interpolated line runs long. The
 *  limits are per-locale display characters (code points): zh lines are dense
 *  at 12; Latin lines run long, so en gets its own clamp at 24 (DR-R2). */
export const PEAK_STAMP_MAX_ZH = 12;
export const PEAK_STAMP_MAX_EN = 24;

export function stampShrinks(line: string, locale: 'zh' | 'en'): boolean {
  return [...line].length > (locale === 'zh' ? PEAK_STAMP_MAX_ZH : PEAK_STAMP_MAX_EN);
}
